// Customer onboarding — an in-app "How to use Talabya" tutorial.
// 5 slides with illustrated mini-mockups of the actual app screens.
// Opens automatically for first-time customers (stored in localStorage) and
// manually via the "كيف أستخدم التطبيق؟" button on the Home page.
import { useState, useEffect } from "react";
import {
  X, ChevronLeft, ChevronRight, Store, ShoppingCart, MapPin, CheckCircle2, Bell, Wallet, Sparkles,
} from "lucide-react";

const SLIDES = [
  {
    key: "browse",
    title: "تصفّح المحلات",
    text: "اختر من قائمة المحلات: مطاعم، مواد غذائية، حلويات وأكثر — كلها داخل جبل نفوسة.",
    icon: Store,
    visual: "browse",
  },
  {
    key: "cart",
    title: "أضف إلى السلة",
    text: "اضغط على الصنف لإضافته إلى سلة التسوق. يمكنك تعديل الكمية أو حذف صنف قبل الإرسال.",
    icon: ShoppingCart,
    visual: "cart",
  },
  {
    key: "checkout",
    title: "أكمل طلبك",
    text: "أدخل عنوانك، اختر التوصيل أو الاستلام، ثم ادفع نقداً عند الاستلام أو من محفظتك.",
    icon: CheckCircle2,
    visual: "checkout",
  },
  {
    key: "track",
    title: "تابع طلبك مباشرةً",
    text: "بعد قبول السائق سترى تحرّكه على الخريطة لحظة بلحظة، مع تحديد الوقت المتوقع للوصول.",
    icon: MapPin,
    visual: "track",
  },
  {
    key: "wallet",
    title: "المحفظة والمكافآت",
    text: "اشحن رصيدك بكروت طلبية، اربح نقاط ولاء، وادعُ أصدقاءك واربح 1 د.ل لكل صديق يُتم أول طلب.",
    icon: Wallet,
    visual: "wallet",
  },
];

const HOW_TO_KEY = "talabya_how_to_seen_v1";

export function shouldShowHowTo() {
  try { return !localStorage.getItem(HOW_TO_KEY); } catch { return false; }
}

export function markHowToSeen() {
  try { localStorage.setItem(HOW_TO_KEY, "1"); } catch { /* ignore */ }
}

export default function HowToUseModal({ onClose }) {
  const [idx, setIdx] = useState(0);
  const slide = SLIDES[idx];
  const isLast = idx === SLIDES.length - 1;

  useEffect(() => { markHowToSeen(); }, []);

  const next = () => (isLast ? onClose() : setIdx((i) => Math.min(i + 1, SLIDES.length - 1)));
  const prev = () => setIdx((i) => Math.max(i - 1, 0));

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
         onClick={onClose} data-testid="howto-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white w-full sm:max-w-md rounded-3xl shadow-2xl overflow-hidden">
        {/* Close */}
        <div className="flex justify-between items-center p-4 border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-primary-brand"/>
            <span className="font-black text-sm" style={{fontFamily:"Cairo"}}>كيف تستخدم طلبية؟</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-[var(--surface-alt)] flex items-center justify-center"
                  data-testid="howto-close">
            <X size={16}/>
          </button>
        </div>

        {/* Slide */}
        <div className="p-6 text-center" data-testid={`howto-slide-${slide.key}`}>
          <VisualMock kind={slide.visual}/>
          <div className="mt-5 flex items-center justify-center w-11 h-11 rounded-2xl bg-primary-brand text-white mx-auto">
            <slide.icon size={22}/>
          </div>
          <h3 className="font-black text-lg mt-3" style={{fontFamily:"Cairo"}}>{slide.title}</h3>
          <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">{slide.text}</p>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 pb-3">
          {SLIDES.map((s, i) => (
            <button key={s.key} onClick={() => setIdx(i)}
                    className={`h-1.5 rounded-full transition-all ${i === idx ? "w-7 bg-primary-brand" : "w-1.5 bg-[var(--border)]"}`}
                    data-testid={`howto-dot-${i}`}/>
          ))}
        </div>

        {/* Nav */}
        <div className="p-4 pt-2 border-t border-[var(--border)] flex items-center gap-2">
          <button onClick={prev} disabled={idx === 0}
                  className="w-11 h-11 rounded-xl bg-[var(--surface-alt)] flex items-center justify-center disabled:opacity-30"
                  data-testid="howto-prev">
            <ChevronRight size={18}/>
          </button>
          <button onClick={next}
                  className="flex-1 py-3 rounded-xl bg-primary-brand text-white font-black text-sm flex items-center justify-center gap-1.5 hover:opacity-90 talabya-shadow-float"
                  data-testid="howto-next">
            {isLast ? "لنبدأ 🚀" : "التالي"} {!isLast && <ChevronLeft size={16}/>}
          </button>
          {!isLast && (
            <button onClick={onClose}
                    className="text-xs text-[var(--text-secondary)] hover:text-primary-brand px-2"
                    data-testid="howto-skip">
              تخطّي
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ────────── Screen mockups (mini "screenshots" built with divs) ──────────
function VisualMock({ kind }) {
  return (
    <div className="mx-auto w-[220px] h-[300px] rounded-[26px] bg-[#0F172A] p-2 talabya-shadow-float">
      <div className="w-full h-full rounded-[20px] bg-white overflow-hidden relative">
        <div className="absolute top-1 left-1/2 -translate-x-1/2 w-14 h-1 bg-gray-800 rounded-full"/>
        <div className="p-3 pt-4">
          {kind === "browse" && <MockBrowse/>}
          {kind === "cart" && <MockCart/>}
          {kind === "checkout" && <MockCheckout/>}
          {kind === "track" && <MockTrack/>}
          {kind === "wallet" && <MockWallet/>}
        </div>
      </div>
    </div>
  );
}

function MockBrowse() {
  const chips = ["الكل", "مطاعم", "بقالة", "حلويات"];
  const shops = [
    { name: "برجر هاوس", tag: "مطعم", tone: "#EF4444" },
    { name: "بقالة الوفاء", tag: "بقالة", tone: "#10B981" },
    { name: "حلويات جبل نفوسة", tag: "حلويات", tone: "#F59E0B" },
  ];
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1 bg-[var(--surface-alt)] rounded-lg px-2 py-1 text-[8px] text-[var(--text-secondary)]">🔍 ابحث عن محل...</div>
      <div className="flex gap-1 overflow-hidden">
        {chips.map((c, i) => (
          <span key={c} className={`text-[7px] px-1.5 py-0.5 rounded-full font-black ${i === 0 ? "bg-primary-brand text-white" : "bg-[var(--surface-alt)]"}`}>{c}</span>
        ))}
      </div>
      {shops.map((s) => (
        <div key={s.name} className="flex items-center gap-1.5 bg-white border border-[var(--border)] rounded-lg p-1.5">
          <div className="w-8 h-8 rounded-md" style={{background: s.tone}}/>
          <div className="flex-1 min-w-0">
            <div className="text-[8px] font-black truncate">{s.name}</div>
            <div className="text-[6px] text-[var(--text-secondary)]">{s.tag} · ⭐ 4.8</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MockCart() {
  return (
    <div className="space-y-1.5">
      <div className="text-[9px] font-black">🛒 سلة التسوق</div>
      {["برجر لحم × 2", "بطاطس مقلية", "بيبسي 500مل"].map((n, i) => (
        <div key={i} className="flex items-center justify-between bg-[var(--surface-alt)] rounded-md px-2 py-1">
          <span className="text-[7px] font-bold">{n}</span>
          <span className="text-[7px] font-black text-primary-brand">15 د.ل</span>
        </div>
      ))}
      <div className="border-t border-[var(--border)] pt-1.5 flex justify-between text-[8px] font-black">
        <span>الإجمالي</span><span className="text-primary-brand">45 د.ل</span>
      </div>
      <div className="mt-1 py-1.5 rounded-md bg-primary-brand text-white text-[8px] font-black text-center">إتمام الطلب</div>
    </div>
  );
}

function MockCheckout() {
  return (
    <div className="space-y-1.5">
      <div className="text-[9px] font-black">📍 الدفع والتوصيل</div>
      <div className="bg-[var(--surface-alt)] rounded-md px-2 py-1 text-[7px]">
        <div className="font-bold">العنوان: يفرن — شارع النجاح</div>
      </div>
      <div className="flex gap-1">
        <div className="flex-1 py-1 rounded-md bg-primary-brand text-white text-[7px] font-black text-center">توصيل</div>
        <div className="flex-1 py-1 rounded-md bg-white border border-[var(--border)] text-[7px] font-bold text-center">استلام</div>
      </div>
      <div className="flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-md p-1.5">
        <div className="w-4 h-4 rounded-full bg-primary-brand"/>
        <span className="text-[7px] font-black">نقداً عند الاستلام</span>
      </div>
      <div className="py-1.5 rounded-md bg-primary-brand text-white text-[8px] font-black text-center">تأكيد الطلب</div>
    </div>
  );
}

function MockTrack() {
  return (
    <div className="space-y-1.5">
      <div className="text-[9px] font-black">🚴 تتبع الطلب</div>
      <div className="relative w-full h-[110px] rounded-md bg-gradient-to-br from-blue-100 to-blue-50 overflow-hidden">
        <div className="absolute top-2 right-2 text-[6px] bg-white/80 rounded px-1">جبل نفوسة</div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-lg">🚴</div>
        <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
          <path d="M 15 80 Q 40 50 55 55 T 85 25" stroke="#2547D0" strokeWidth="2" fill="none" strokeDasharray="3,2"/>
          <circle cx="15" cy="80" r="3" fill="#10B981"/>
          <circle cx="85" cy="25" r="3" fill="#EF4444"/>
        </svg>
      </div>
      <div className="flex justify-between text-[7px]">
        <span className="font-bold">🕒 12 دقيقة</span>
        <span className="text-primary-brand font-black">في الطريق</span>
      </div>
    </div>
  );
}

function MockWallet() {
  return (
    <div className="space-y-1.5">
      <div className="rounded-md p-2 text-white" style={{background:"linear-gradient(135deg,#1E3A8A,#2547D0)"}}>
        <div className="text-[7px] opacity-80">رصيدي</div>
        <div className="font-black text-sm mt-0.5">45.00 د.ل</div>
        <div className="text-[6px] opacity-80 mt-0.5">•••• 1234</div>
      </div>
      <div className="flex gap-1">
        <div className="flex-1 py-1 rounded-md bg-[var(--surface-alt)] text-[6px] font-black text-center flex items-center justify-center gap-0.5">🎟️ شحن</div>
        <div className="flex-1 py-1 rounded-md bg-[var(--surface-alt)] text-[6px] font-black text-center flex items-center justify-center gap-0.5">⭐ نقاط</div>
      </div>
      <div className="bg-emerald-50 border border-emerald-200 rounded-md p-1.5">
        <div className="text-[7px] font-black text-emerald-700">🎁 كود الإحالة</div>
        <div className="text-[9px] font-black tracking-wider" style={{fontFamily:"monospace"}}>M93QA3DF</div>
      </div>
    </div>
  );
}
